import { Injectable, InputSignal, signal, Type } from '@angular/core';
import { assert, assertIsDefined } from 'app/utils/assert';
import { BehaviorSubject } from 'rxjs';

export type TSlideOutWidth = '350' | '600' | '800';
export type TSlideOutCssWidth = `${TSlideOutWidth}px`;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TAnyComponent<T = any> = Type<T>;

type TInputType<T> = {
  [K in keyof T]: T[K] extends InputSignal<infer U> ? U : T[K];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TDynamicComponentConfig<T = any> = {
  component: TAnyComponent<T>;
  inputs?: Partial<TInputType<T>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  outputs?: Partial<Record<keyof T, (event: any) => void>>;
};

export type TSlideOutButton<T = TAnyComponent> = {
  label: string;
  variant: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'anchor' | 'danger';
  action: (component: T) => void;
};

@Injectable({ providedIn: 'root' })
export class SlideOutService {
  private readonly isOpen_ = signal(false);
  private readonly width_ = signal<TSlideOutCssWidth>('800px');
  private readonly title_ = signal<string>('');
  private readonly subtitle_ = signal<string | null>(null);
  private readonly headerPillText_ = signal<string | null>(null);
  private readonly content_ = signal<TAnyComponent | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly buttons_ = signal<TSlideOutButton<any>[]>([]);

  private readonly contentChanged$ = new BehaviorSubject<TDynamicComponentConfig | null>(null);
  public readonly contentChanged = this.contentChanged$.asObservable();

  public getIsOpen(): boolean {
    return this.isOpen_();
  }

  public setWidth(width: TSlideOutCssWidth): this {
    assert(width.replace('px', '').length > 0 && width.includes('px'), 'length should be a non-empty string');
    assert(parseInt(width.replace('px', ''), 10) > 0, 'length should be a positive number');
    this.width_.set(width);
    return this;
  }

  public getWidth(): TSlideOutCssWidth {
    return this.width_();
  }

  public setTitle(text: string): this {
    assertIsDefined(text);
    this.title_.set(text);
    return this;
  }

  public getTitle(): string {
    return this.title_();
  }

  public setSubTitle(text: string | null): this {
    this.subtitle_.set(text);
    return this;
  }

  public getSubTitle(): string | null {
    return this.subtitle_();
  }

  public setHeaderPillText(text: string | null): this {
    if (text === null) {
      this.headerPillText_.set(null);
      return this;
    }

    const normalizedText = text.trim();
    this.headerPillText_.set(normalizedText === '' ? null : normalizedText);
    return this;
  }

  public getHeaderPillText(): string | null {
    return this.headerPillText_();
  }

  public getHeaderPillColor(): 'green' | 'red' {
    return this.headerPillText_()?.toLowerCase() === 'open' ? 'green' : 'red';
  }

  public setContent<T>(config: TDynamicComponentConfig<T>): this {
    if (!this.content_()) {
      this.content_.set(config.component);
      this.contentChanged$.next(config);
    }

    return this;
  }

  public getContent(): TAnyComponent | null {
    return this.content_();
  }

  public setButtons<T>(buttons: TSlideOutButton<T>[]): this {
    this.buttons_.set(buttons);
    return this;
  }

  public getButtons(): TSlideOutButton[] {
    return this.buttons_();
  }

  public reset(): this {
    this.setTitle('');
    this.setSubTitle(null);
    this.setHeaderPillText(null);
    this.setButtons([]);
    this.content_.set(null);
    this.contentChanged$.next(null);

    return this;
  }

  public open(): void {
    this.isOpen_.set(true);
  }

  public close(): void {
    this.isOpen_.set(false);
  }
}
